/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { TierModel } from '../../modules/centralisedManagement/shared/models';
import { VendorEnum } from '../../modules/centralisedManagement/shared/enums';

@Injectable()
export class PortalMockService {
    /**
     * Mock ADCs service
     */
    public adcsApiService(): any {
        return (
            class AdcsApiService {
                getAll(): Observable<any> {
                    return of(null);
                }

                delete(): Observable<any> {
                    return of(null);
                }

                systemUpdate(): Observable<any> {
                    return of(null);
                }
            }
        );
    }

    /**
     * Mock ADC utils
     */
    public accountsApiService(): any {
        return (
            class AccountsApiService {
                getHubspotAccount(id: string): Observable<any> {
                    return of
                    ({
                        id,
                        firstname: 'father',
                        lastname: 'ted',
                        email: 'father.ted@craggyisland.org'
                    } as any);
                }

                get(id: string): Observable<any> {
                    return of({
                        id,
                        firstName: 'father',
                        lastName: 'ted',
                        email: 'father.ted@craggyisland.org'
                    } as any);
                }
            }
        );
    }

    /**
     * Mock ADC utils
     */
    public adcsUtilsService(): any {
        return (
            class AdcsUtilsService {
                getRecommendationText(operationalRisk: string): string {
                    switch (operationalRisk?.toLowerCase()) {
                        case 'critical':
                            return 'Upgrade to the latest version immediately and apply any available patches.';
                        case 'high':
                            return 'Apply the latest security patches and consider additional mitigations.';
                        case 'medium':
                            return 'Apply relevant security patches and follow best practices to minimize risks.';
                        case 'low':
                            return 'Monitor the situation and apply necessary updates or changes if deemed appropriate.';
                        default:
                            return 'No specific remediation recommendation available.';
                    }
                }

                public versionCanUpdate(row: any): boolean {
                    if (row.vendor === VendorEnum.LBENTERPRISE) {
                        const cleanVersion: string = row.version?.startsWith('v') ? row.version.slice(1) : row.version;

                        return this.compareDecimals(cleanVersion, '8.13.0', '>=');
                    } else {
                        return true;
                    }
                }

                // Repeated here due to dependency injection issues.
                public compareDecimals(firstNumber: string, secondNumber: string, operation: string): boolean {
                    const a: number = parseFloat(firstNumber);
                    const b: number = parseFloat(secondNumber);

                    switch (operation) {
                        case '<':
                            return a < b;
                        case '<=':
                            return a <= b;
                        case '>':
                            return a > b;
                        case '>=':
                            return a >= b;
                        case '===':
                            return a === b;
                    }
                }
            }
        );
    }

    /**
     * Mock Backup API service.
     */
    public backupApiService(): any {
        return (
            class BackupApiService {
                backupAdc(): Observable<any> {
                    return new Observable<any>();
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                getBackupsByNamespaceId(namespaceId: number, options?: any): Observable<any> {
                    return new Observable<any>();
                }
            }
        );
    }

    /**
     * Mock namespacesApiService
     */
    public namespacesApiService(): any {
        return (
            class MockNamespacesApiService {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                edit(orgId, namespaceId, { name: name }): any {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                create(orgId, { name: name }): any {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                delete(orgId: any, nameSpaceId): any {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                getAll(orgId: any): Observable<any> {
                    return new Observable<any>();
                }
            }
        );
    }

    /**
     * Mock Dashboard service.
     */
    public dashboardService(): any {
        return (
            class DashboardService {
                allDataEmitter: EventEmitter<any> = new EventEmitter<any>();
                loadingEmitter: EventEmitter<any> = new EventEmitter<any>();
                selectedTier = { adcs: 12, backups: 25, support: 'better', tier: 'professional' };

                tiers = {
                    free: <TierModel>{ adcs: 6, backups: 2, support: 'basic', tier: 'free' },
                    professional: <TierModel>{ adcs: 12, backups: 25, support: 'better', tier: 'professional' },
                    business: <TierModel>{ adcs: 30, backups: 0, support: 'best', tier: 'business' },
                    tailored: <TierModel>{ adcs: 0, backups: 0, support: 'best', tier: 'tailored' }
                };

                allData: any = {
                    adcs: [],
                    backups: [],
                    insights: [],
                    namespaces: [],
                    organisation: {
                        contactNumber: '123',
                        id: '456',
                        name: '789',
                        ownerID: 1
                    },
                    schedules: [],
                    tier: this.tiers['professional'],
                    users: []
                };
            }
        );
    }

    /**
     * Mock key store service
     */
    public keyStoreService(): any {
        return (
            class MockKeyStoreService {
                storeState = '';
                userPrivateKey = '';
            }
        );
    }

    /**
     * Mock namespace service
     */
    public namespaceService(): any {
        return (
            class MockNamespaceService {
                selectedEmitter: Subject<any> = new Subject<any>();
                _namespaces = [
                    { id: 2, name: 'default' },
                    { id: 47, name: 'test namespace' }
                ];

                get selectedNamespace(): any {
                    return { key: 0, value: 'All' };
                }

                get namespaces(): any[] {
                    return this._namespaces;
                }
            }
        );
    }

    /**
     * Mock namespace service
     */
    public rbacService(): any {
        return (
            class MockRbacService {
                _selectedTier: TierModel = { adcs: 6, backups: 2, support: 'basic', tier: 'free' };

                get selectedTier(): any {
                    return this._selectedTier;
                }
            }
        );
    }
}

